/**
 * NarrativeLog — live and playback narrative text display for PrimateVisionV2.
 *
 * Renders NarrativeEntry[] as a scrollable text log, newest entry first.
 * "status" type entries (e.g. "Still watching…") are always shown at the bottom.
 * Supports playback time filtering, keyword highlighting, and click-to-seek.
 *
 * Styling uses the PrimateVisionV2 forced-light-mode L palette (hardcoded).
 * Font: Inter / system-ui throughout — no monospace font anywhere in this component.
 */

import React from 'react'
import type { NarrativeEntry } from '../../lib/narrative'
import { trackNarrativeFeedback } from '../../telemetry/events'
import { sendNarrativeFeedback } from '../../lib/api/misc'

// ── Light-mode palette (matches PrimateVisionV2's L object) ─────────────────
const L = {
  textPrimary: '#1A1916',
  textMuted: '#7A7669',
}

// ── Keyword highlight span ────────────────────────────────────────────────────
// Highlight whole words AND simple plural forms of the user's term ("table" → also
// matches "tables"). This prevents the regex from splitting INSIDE a plural and
// rendering a detached trailing "s" outside the pill (e.g. "[table] s").
function highlightTerms(text: string, terms: string[]): React.ReactNode {
  if (!terms.length) return text

  // Build a single case-insensitive regex that matches each term as a whole word,
  // optionally followed by a plural "s" or "es", so pluralised forms render as one pill.
  const escaped = terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const regex = new RegExp(`\\b(${escaped.join('|')})(?:es|s)?\\b`, 'gi')

  // Use matchAll so we keep both the matched form (singular or plural) and the
  // surrounding text. text.split would only return the captured group, dropping
  // the trailing pluralising "s".
  const out: React.ReactNode[] = []
  let lastIndex = 0
  let key = 0
  for (const m of text.matchAll(regex)) {
    const start = m.index ?? 0
    if (start > lastIndex) out.push(text.slice(lastIndex, start))
    out.push(
      <span
        key={`hl-${key++}`}
        style={{
          background: 'rgba(37,99,235,0.12)',
          borderRadius: 2,
          padding: '0 2px',
        }}
      >
        {m[0]}
      </span>,
    )
    lastIndex = start + m[0].length
  }
  if (lastIndex < text.length) out.push(text.slice(lastIndex))
  return out
}

// ── CSS for pulsing status entries (injected once) ────────────────────────────
let narrativePulseInjected = false
function ensureNarrativePulseStyle() {
  if (narrativePulseInjected || typeof document === 'undefined') return
  narrativePulseInjected = true
  const style = document.createElement('style')
  style.textContent = `
    @keyframes narrativePulse {
      0%, 100% { opacity: 0.5; }
      50% { opacity: 1; }
    }
    .narrative-pulse {
      animation: narrativePulse 2s ease-in-out infinite;
    }
  `
  document.head.appendChild(style)
}

// ── Entry style by type ───────────────────────────────────────────────────────
function entryColor(type: NarrativeEntry['type']): string {
  switch (type) {
    case 'action':        return '#D97706'  // amber
    case 'count':         return '#2563EB'  // blue
    case 'summary':       return L.textMuted
    case 'status':        return L.textMuted
    case 'prompt_change': return L.textMuted
    default:              return L.textPrimary  // detection, absence
  }
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface NarrativeLogProps {
  entries: NarrativeEntry[]
  isLive: boolean
  currentVideoTime?: number          // for playback sync filtering (seconds)
  isPlayback?: boolean
  searchTerms?: string[]             // for keyword highlighting
  videoRef?: React.RefObject<HTMLVideoElement | null>  // for click-to-seek
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function NarrativeLog({
  entries,
  isLive,
  currentVideoTime,
  isPlayback,
  searchTerms = [],
  videoRef,
}: NarrativeLogProps) {
  // Inject pulse animation CSS once
  React.useEffect(() => { ensureNarrativePulseStyle() }, [])

  // Feedback vote tracking
  const [votedSet, setVotedSet] = React.useState<Set<number>>(new Set())
  const handleVote = React.useCallback((index: number, verdict: 'up' | 'down') => {
    setVotedSet(prev => new Set(prev).add(index))
    // Analytics
    trackNarrativeFeedback({ verdict, entry_index: index, session_type: isLive ? 'stream' : 'upload' })
    // R15 (PRI-439): endpoint literal moved to the API layer.
    sendNarrativeFeedback({
      verdict,
      narrative_text: entries[index]?.text ?? '',
      entry_index: index,
      search_terms: searchTerms,
    })
  }, [entries, searchTerms])

  // Empty state — render nothing
  if (!entries.length) return null

  // Separate status entries from the rest
  const statusEntries = entries.filter(e => e.type === 'status')
  const mainEntries = entries.filter(e => e.type !== 'status')

  // Playback time filtering — only show entries up to currentVideoTime.
  // Prefer recording_elapsed_ms (ms from recording start → /1000 = seconds, matches video.currentTime).
  // Fall back to window_start_elapsed_s (server elapsed seconds) for old data without recording_elapsed_ms.
  const visibleMain = (isPlayback && currentVideoTime != null)
    ? mainEntries.filter(e => {
        const t = e.recording_elapsed_ms != null
          ? e.recording_elapsed_ms / 1000
          : e.window_start_elapsed_s
        return t <= currentVideoTime
      })
    : mainEntries

  // Entries are already newest-first from accumulation (caller prepends)
  // Status entries go at the bottom (persistent "Still watching…" indicator)
  const latestStatus = statusEntries.length > 0 ? statusEntries[0] : null

  const terms = searchTerms ?? []

  return (
    <div
      data-testid="narrative-log"
      style={{
        overflowY: 'auto',
        maxHeight: 240,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      {visibleMain.map((entry, i) => (
        <NarrativeRow
          key={`${entry.wall_clock}-${entry.elapsed_s}-${i}`}
          entry={entry}
          searchTerms={terms}
          videoRef={videoRef}
          index={i}
          votedSet={votedSet}
          onVote={handleVote}
        />
      ))}

      {latestStatus && (
        <NarrativeRow
          key={`status-bottom-${latestStatus.wall_clock}`}
          entry={latestStatus}
          searchTerms={terms}
          videoRef={videoRef}
          forceBottom
          index={-1}
          votedSet={votedSet}
          onVote={handleVote}
        />
      )}

      {/* Live indicator — tiny pulse dot when stream is live */}
      {isLive && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, paddingTop: 2 }}>
          <span style={{
            display: 'inline-block', width: 5, height: 5, borderRadius: '50%',
            background: '#16A34A', animation: 'narrativePulse 1.5s ease-in-out infinite',
          }} />
          <span style={{ fontSize: 10, color: L.textMuted, fontFamily: 'Inter, system-ui, sans-serif' }}>
            live
          </span>
        </div>
      )}
    </div>
  )
}

// ── Single entry row ──────────────────────────────────────────────────────────
function NarrativeRow({
  entry,
  searchTerms,
  videoRef,
  forceBottom: _forceBottom,
  index,
  votedSet,
  onVote,
}: {
  entry: NarrativeEntry
  searchTerms: string[]
  videoRef?: React.RefObject<HTMLVideoElement | null>
  forceBottom?: boolean
  index: number
  votedSet: Set<number>
  onVote: (index: number, verdict: 'up' | 'down') => void
}) {
  const color = entryColor(entry.type)
  const isStatus = entry.type === 'status'
  const isSummary = entry.type === 'summary'
  const isPromptChange = entry.type === 'prompt_change'
  const canSeek = videoRef != null && entry.recording_elapsed_ms != null
  const voted = votedSet.has(index)
  const [hovered, setHovered] = React.useState(false)
  const [voteResult, setVoteResult] = React.useState<'up' | 'down' | null>(null)

  function handleTimestampClick() {
    if (videoRef?.current && entry.recording_elapsed_ms != null) {
      videoRef.current.currentTime = entry.recording_elapsed_ms / 1000
    }
  }

  function handleVote(verdict: 'up' | 'down') {
    if (voted) return
    setVoteResult(verdict)
    onVote(index, verdict)
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={isPromptChange ? 'prompt-change-marker' : 'narrative-entry'}
      style={{
        fontSize: isPromptChange ? 12 : 13,
        lineHeight: 1.5,
        color,
        fontFamily: 'Inter, system-ui, sans-serif',
        fontStyle: isSummary || isPromptChange ? 'italic' : 'normal',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 4,
        ...(isStatus ? { opacity: 0.6 } : {}),
      }}
      className={isStatus ? 'narrative-pulse' : undefined}
    >
      <div style={{ flex: 1 }}>
        {/* Timestamp prefix — clickable if video ref available */}
        <span
          onClick={canSeek ? handleTimestampClick : undefined}
          style={{
            fontSize: 11,
            color: L.textMuted,
            fontFamily: 'Inter, system-ui, sans-serif',
            cursor: canSeek ? 'pointer' : 'default',
            textDecoration: canSeek ? 'underline' : 'none',
            userSelect: 'none',
            marginRight: 2,
          }}
        >
          {entry.wall_clock} —{' '}
        </span>
        {/* Entry text — with optional keyword highlighting */}
        {searchTerms.length > 0
          ? highlightTerms(entry.text, searchTerms)
          : entry.text
        }
      </div>
      {/* Feedback buttons — appear on hover, hide for status entries */}
      {!isStatus && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0,
          opacity: voted ? 1 : (hovered ? 0.7 : 0),
          transition: 'opacity 150ms',
          pointerEvents: voted ? 'none' : 'auto',
        }}>
          {voteResult ? (
            <span style={{ fontSize: 10, color: L.textMuted, fontFamily: 'Inter, system-ui, sans-serif' }}>
              {voteResult === 'up' ? '✓' : 'Flagged'}
            </span>
          ) : (
            <>
              <button
                onClick={() => handleVote('up')}
                title="Correct"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: 12, lineHeight: 1, color: L.textMuted }}
              >
                👍
              </button>
              <button
                onClick={() => handleVote('down')}
                title="Incorrect"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: 12, lineHeight: 1, color: L.textMuted }}
              >
                👎
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
